import React, { useEffect, useState } from "react";
import { UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import CountryPhoneInput from "@/components/CountryPhoneInput";
import { useAuth } from "@/context/AuthContext";
import { useStations } from "@/components/supply/shared";

const ROLE_OPTIONS = [
  { value: "admin",         label: "Admin" },
  { value: "operator",      label: "Operator" },
  { value: "field_officer", label: "Field Officer" },
  { value: "viewer",        label: "Viewer" },
  { value: "station_staff", label: "Stabex station staff" },
];

const RolePill = ({ role }) => {
  const map = {
    admin:         "bg-flame-50 text-flame-700 border border-flame-200",
    operator:      "bg-brand-50 text-brand-700 border border-brand-200",
    field_officer: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    viewer:        "bg-slate-100 text-slate-600 border border-slate-200",
    station_staff: "bg-blue-50 text-blue-700 border border-blue-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold ${map[role] || map.viewer}`}>
      {role === "field_officer" ? "field" : role === "station_staff" ? "station" : role}
    </span>
  );
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "", phone: "", email: "", password: "", role: "operator",
  });
  const [err, setErr] = useState("");
  const [stations] = useStations();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.data || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load users");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ name: "", phone: "", email: "", password: "", role: "operator" });
    setErr("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("Full name required"); return; }
    if (!form.phone || form.phone.length < 8) { setErr("Valid phone number required"); return; }
    if (!form.password || form.password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (form.role === "station_staff" && !form.station_id) { setErr("Choose the station this person works at"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.name} created`);
      setOpen(false); resetForm();
      load();
    } catch (e) {
      setErr(formatDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (+${u.phone})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Team</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-sm text-slate-500 mt-1">Team accounts for Gasify Console — admins, operators, field officers and viewers. Phone number is the username.</p>
        </div>
        <Button
          onClick={() => { resetForm(); setOpen(true); }}
          data-testid="users-create-btn"
          className="bg-brand-600 hover:bg-brand-700 rounded-sm">
          <UserPlus size={16} className="mr-2" /> Create user
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Phone</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Role</th>
              <th className="text-left px-4 py-3 font-semibold">Created</th>
              <th className="text-right px-4 py-3 font-semibold w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No users yet.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`users-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">+{u.phone}</td>
                <td className="px-4 py-3 text-slate-600">{u.email || <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3"><RolePill role={u.role} /></td>
                <td className="px-4 py-3 text-slate-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right">
                  {me?.id !== u.id && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => del(u)}
                      data-testid={`users-delete-btn-${u.id}`}
                      className="h-8 rounded-sm">
                      <Trash2 size={13} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-sm" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create user</DialogTitle>
            <DialogDescription>They will sign in using their phone number.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Full name</Label>
              <Input
                data-testid="create-user-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
                className="mt-1 rounded-sm"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Phone number</Label>
              <div className="mt-1">
                <CountryPhoneInput
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  testId="create-user-phone"
                />
              </div>
              <div className="text-[10px] text-slate-400 mt-1">This is the user's login username.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Email <span className="text-slate-400 normal-case">(optional)</span></Label>
              <Input
                data-testid="create-user-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Password</Label>
              <Input
                data-testid="create-user-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 6 characters"
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Role</Label>
              <select
                data-testid="create-user-role-select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 h-10 w-full border border-slate-300 rounded-sm px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
              >
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {form.role === "station_staff" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Station</Label>
                <select
                  data-testid="create-user-station-select"
                  value={form.station_id || ""}
                  onChange={(e) => setForm({ ...form, station_id: e.target.value })}
                  className="mt-1 h-10 w-full border border-slate-300 rounded-sm px-3 text-sm bg-white"
                >
                  <option value="">Select station</option>
                  {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            {err && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2 rounded-sm" data-testid="create-user-error">
                {err}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="create-user-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-brand-600 hover:bg-brand-700 rounded-sm" data-testid="create-user-submit">
                {saving ? "Creating…" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
